/**
 * Cookie Backup Utility
 *
 * Provides a fallback mechanism for localStorage by mirroring auth-related
 * values to cookies. This prevents unexpected logout when browsers clear localStorage.
 *
 * Usage:
 * - On login: call backupToCookie(key, value) after each localStorage.setItem
 * - On logout: call removeFromCookie(key) after each localStorage.removeItem
 * - On app init: call rehydrateFromCookies() to restore missing localStorage values
 */

// Keys that should be backed up to cookies
const AUTH_KEYS = [
  "access_token",
  "access_token_vr",
  "expires_in",
  "selectedRegion",
  "selectedCurrency",
];

// Cookie expiration: 1 year
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

/**
 * Set a cookie with the given key and value
 * @param {string} key - Cookie name (same as localStorage key)
 * @param {string} value - Cookie value
 */
export function backupToCookie(key, value) {
  if (!key || value === null || value === undefined) return;

  try {
    const encodedValue = encodeURIComponent(value);
    document.cookie = `${key}=${encodedValue}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch (e) {
    console.warn("[CookieBackup] Failed to set cookie:", key, e);
  }
}

/**
 * Get a cookie value by key
 * @param {string} key - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export function getCookie(key) {
  if (!key) return null;

  try {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const [cookieKey, ...cookieValueParts] = cookie.trim().split("=");
      if (cookieKey === key) {
        const cookieValue = cookieValueParts.join("=");
        return decodeURIComponent(cookieValue);
      }
    }
  } catch (e) {
    console.warn("[CookieBackup] Failed to get cookie:", key, e);
  }
  return null;
}

/**
 * Remove a cookie by key
 * @param {string} key - Cookie name
 */
export function removeFromCookie(key) {
  if (!key) return;

  try {
    document.cookie = `${key}=; path=/; max-age=0; SameSite=Lax`;
  } catch (e) {
    console.warn("[CookieBackup] Failed to remove cookie:", key, e);
  }
}

/**
 * Backup a localStorage value to cookie (call after localStorage.setItem)
 * Only backs up if the key is in AUTH_KEYS list
 * @param {string} key - localStorage key
 * @param {string} value - Value to backup
 */
export function backupAuthValue(key, value) {
  if (AUTH_KEYS.includes(key)) {
    backupToCookie(key, value);
  }
}

/**
 * Remove auth value from cookie (call after localStorage.removeItem)
 * @param {string} key - localStorage key
 */
export function removeAuthBackup(key) {
  if (AUTH_KEYS.includes(key)) {
    removeFromCookie(key);
  }
}

/**
 * Clear all auth-related cookies (call on logout)
 */
export function clearAllAuthCookies() {
  AUTH_KEYS.forEach((key) => {
    removeFromCookie(key);
  });
}

/**
 * Rehydrate localStorage from cookies
 * Should be called once at app initialization (before any auth checks)
 * Only restores values that are missing from localStorage but exist in cookies
 */
export function rehydrateFromCookies() {
  let restored = false;

  AUTH_KEYS.forEach((key) => {
    try {
      const localValue = localStorage.getItem(key);

      // Only restore if localStorage is missing this value
      if (localValue === null || localValue === undefined) {
        const cookieValue = getCookie(key);

        if (
          cookieValue !== null &&
          cookieValue !== undefined &&
          cookieValue !== ""
        ) {
          localStorage.setItem(key, cookieValue);
          restored = true;
          console.info("[CookieBackup] Restored from cookie:", key);
        }
      }
    } catch (e) {
      console.warn("[CookieBackup] Failed to rehydrate:", key, e);
    }
  });

  return restored;
}

/**
 * Sync current localStorage auth values to cookies
 * Useful for initial setup or ensuring cookies are in sync
 */
export function syncLocalStorageToCookies() {
  AUTH_KEYS.forEach((key) => {
    try {
      const value = localStorage.getItem(key);
      if (value !== null && value !== undefined) {
        backupToCookie(key, value);
      }
    } catch (e) {
      console.warn("[CookieBackup] Failed to sync:", key, e);
    }
  });
}

export { AUTH_KEYS };
